(() => {
  const orderBySize = (variants) => {
    const clothing = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '4xl', '5xl'];
    const parsed = variants.map((variant, index) => {
      const label = variant.title.toLowerCase().replace(/\s+/g, '').replace(',', '.');
      const measure = label.match(/^(\d+(?:\.\d+)?)(ml|cl|l|g|kg)$/);
      if (measure && Number(measure[1]) > 0) {
        const multiplier = { ml: 1, cl: 10, l: 1000, g: 1, kg: 1000 }[measure[2]];
        return { variant, index, family: ['g', 'kg'].includes(measure[2]) ? 'weight' : 'volume', rank: Number(measure[1]) * multiplier };
      }
      const rank = clothing.indexOf(label.replace(/^2xl$/, 'xxl').replace(/^3xl$/, 'xxxl'));
      return { variant, index, family: rank >= 0 ? 'clothing' : null, rank };
    });
    if (!parsed.length || parsed.some((entry) => !entry.family || entry.family !== parsed[0].family)) return variants;
    return parsed.sort((a, b) => a.rank - b.rank || a.index - b.index).map((entry) => entry.variant);
  };
  const init = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    if (root.dataset.testOnly === 'true' && new URLSearchParams(location.search).get('advice_test') !== '1' && !window.Shopify?.designMode) return;
    if (!root.dataset.endpoint?.trim()) return;
    try {
      const configured = new URL(root.dataset.endpoint, location.href);
      const localTest = ['127.0.0.1', 'localhost'].includes(configured.hostname) && (root.dataset.testOnly === 'true' || root.dataset.cartMode === 'test');
      if (configured.username || configured.password || (configured.protocol !== 'https:' && !localTest)) return;
      if (!localTest && ['127.0.0.1', 'localhost'].includes(configured.hostname)) return;
    } catch { return; }
    root.hidden = false;
    const form = root.querySelector('form');
    const input = root.querySelector('textarea');
    const submit = root.querySelector('[type="submit"]');
    const submitLabel = submit.textContent;
    const detail = root.querySelector('[name="detail"]');
    const result = root.querySelector('[data-advice-result]');
    const status = root.querySelector('[data-advice-status]');
    const reset = root.querySelector('[data-advice-reset]');
    const photoInput = root.querySelector('[data-advice-photos]');
    const photoPreviews = root.querySelector('[data-advice-photo-previews]');
    const photoStatus = root.querySelector('[data-advice-photo-status]');
    const history = [];
    const photos = [];
    const temporaryPhotoUrls = new Set();
    const maxPhotos = 3;
    const maxImageBytes = 350000;
    const maxRequestBytes = 1600000;
    let busy = false;
    let preparingPhotos = false;
    let photoGeneration = 0;
    let followup;
    const label = (name) => root.dataset[name] || '';
    const text = (tag, value, className) => {
      const node = document.createElement(tag);
      node.textContent = value;
      if (className) node.className = className;
      return node;
    };
    const updatePhotoControls = () => {
      if (photoInput) photoInput.disabled = busy || preparingPhotos;
      photoPreviews?.querySelectorAll('button').forEach((button) => { button.disabled = busy || preparingPhotos; });
      submit.disabled = busy || preparingPhotos;
      reset.disabled = busy;
      if (followup) followup.button.disabled = busy || preparingPhotos;
      if (photoStatus) photoStatus.setAttribute('aria-busy', preparingPhotos ? 'true' : 'false');
    };
    const renderPhotos = () => {
      if (!photoPreviews) return;
      photoPreviews.replaceChildren();
      photos.forEach((photo, index) => {
        const item = text('li', '', 'nx-advice__photo');
        const image = document.createElement('img');
        image.src = photo.previewUrl;
        image.alt = label('photosPreview') + ' ' + (index + 1);
        image.width = 160;
        image.height = 120;
        const remove = text('button', label('photosRemove') + ' ' + (index + 1));
        remove.type = 'button';
        remove.setAttribute('aria-label', label('photosRemove') + ' ' + (index + 1));
        remove.addEventListener('click', () => {
          if (busy || preparingPhotos) return;
          URL.revokeObjectURL(photo.previewUrl);
          photos.splice(index, 1);
          renderPhotos();
          if (photoStatus) photoStatus.textContent = photos.length ? label('photosReady') + ' ' + photos.length : '';
          photoInput?.focus();
        });
        item.append(image, remove);
        photoPreviews.append(item);
      });
      photoPreviews.hidden = !photos.length;
      updatePhotoControls();
    };
    const clearPhotos = () => {
      photoGeneration += 1;
      photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      photos.length = 0;
      temporaryPhotoUrls.forEach((url) => URL.revokeObjectURL(url));
      temporaryPhotoUrls.clear();
      preparingPhotos = false;
      if (photoInput) photoInput.value = '';
      if (photoStatus) photoStatus.textContent = '';
      renderPhotos();
    };
    const preparePhoto = async (file) => {
      const originalUrl = URL.createObjectURL(file);
      temporaryPhotoUrls.add(originalUrl);
      const image = new Image();
      const canvas = document.createElement('canvas');
      try {
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = () => reject(new Error(label('photosReadError')));
          image.src = originalUrl;
        });
        if (!image.naturalWidth || !image.naturalHeight) throw new Error(label('photosReadError'));
        const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
        let width = Math.max(1, Math.round(image.naturalWidth * scale));
        let height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error(label('photosReadError'));
        for (let attempt = 0; attempt < 8; attempt += 1) {
          canvas.width = width;
          canvas.height = height;
          context.fillStyle = '#fff';
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0, width, height);
          for (const quality of [0.86, 0.7, 0.54, 0.38]) {
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
            if (!blob || blob.type !== 'image/jpeg') throw new Error(label('photosReadError'));
            if (blob.size > maxImageBytes) continue;
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reader.onabort = () => reject(new Error(label('photosReadError')));
              reader.readAsDataURL(blob);
            });
            if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/jpeg;base64,')) throw new Error(label('photosReadError'));
            return { blob, dataUrl };
          }
          width = Math.max(1, Math.round(width * 0.78));
          height = Math.max(1, Math.round(height * 0.78));
        }
        throw new Error(label('photosSizeError'));
      } finally {
        image.src = '';
        canvas.width = canvas.height = 0;
        if (temporaryPhotoUrls.delete(originalUrl)) URL.revokeObjectURL(originalUrl);
      }
    };
    photoInput?.addEventListener('change', async () => {
      if (busy || preparingPhotos) return;
      const files = Array.from(photoInput.files || []);
      photoInput.value = '';
      if (!files.length) return;
      if (photos.length + files.length > maxPhotos) {
        photoStatus.textContent = label('photosMax');
        return;
      }
      if (files.some((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type))) {
        photoStatus.textContent = label('photosUnsupported');
        return;
      }
      const generation = photoGeneration;
      preparingPhotos = true;
      reset.hidden = false;
      photoStatus.textContent = label('photosProcessing');
      updatePhotoControls();
      try {
        const prepared = [];
        for (const file of files) {
          prepared.push(await preparePhoto(file));
          if (generation !== photoGeneration) return;
        }
        prepared.forEach(({ blob, dataUrl }) => photos.push({ dataUrl, previewUrl: URL.createObjectURL(blob) }));
        renderPhotos();
        photoStatus.textContent = label('photosReady') + ' ' + photos.length;
      } catch (error) {
        if (generation === photoGeneration) photoStatus.textContent = error.message || label('photosReadError');
      } finally {
        if (generation === photoGeneration) {
          preparingPhotos = false;
          updatePhotoControls();
        }
      }
    });
    const cartRoot = (window.Shopify?.routes?.root || root.dataset.cartRoot || '/').replace(/\/?$/, '/');
    const testCart = root.dataset.cartMode === 'test';
    const cartPanel = root.querySelector('[data-advice-test-cart]');
    const showTestCart = async () => {
      if (!testCart || !cartPanel) return;
      const response = await fetch(cartRoot + 'cart.js');
      if (!response.ok) throw new Error(label('cartError'));
      const cart = await response.json();
      cartPanel.replaceChildren(text('h3', label('testCartTitle')));
      cart.items.forEach((item) => {
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? ' · ' + item.variant_title : '';
        cartPanel.append(text('p', `${item.quantity} × ${item.product_title}${variant}`));
      });
      cartPanel.hidden = !cart.items.length;
    };
    showTestCart().catch(() => {});
    let cardNumber = 0;
    const addCartControls = async (card, product) => {
      if (!/^[a-z0-9][a-z0-9-]*$/.test(product.handle)) return;
      const controls = text('div', '', 'nx-advice__cart');
      const button = text('button', label('variantsLoading'), 'nx-advice__add');
      button.type = 'button';
      button.disabled = true;
      const message = text('p', '', 'nx-advice__cart-status');
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      const cartLink = text('a', testCart ? label('testCartLink') : label('cartLink'));
      cartLink.href = testCart ? '#advice-test-cart' : cartRoot + 'cart';
      cartLink.hidden = true;
      controls.append(button, message, cartLink);
      card.append(controls);
      try {
        const response = await fetch(cartRoot + 'products/' + encodeURIComponent(product.handle) + '.js', { signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error();
        const catalog = await response.json();
        const variants = orderBySize(catalog.variants?.filter((variant) => Number.isSafeInteger(variant.id) && variant.id > 0) || []);
        if (catalog.handle !== product.handle || !variants?.length) throw new Error();
        // Subscriptions and special quantity rules require the full product form.
        if (catalog.requires_selling_plan || variants.some((variant) => variant.quantity_rule && (variant.quantity_rule.min > 1 || variant.quantity_rule.increment > 1))) {
          button.hidden = true;
          message.textContent = label('productOptions');
          return;
        }
        const money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
        const formatPrice = (cents) => money.format(cents / 100);
        const unitPrice = (variant) => {
          const measurement = variant?.unit_price_measurement;
          if (!measurement || !Number.isFinite(variant.unit_price)) return '';
          return formatPrice(variant.unit_price) + '/' + (measurement.reference_value === 1 ? '' : measurement.reference_value) + measurement.reference_unit;
        };
        const price = text('p', '', 'nx-advice__price');
        const unit = text('p', '', 'nx-advice__unit');
        price.hidden = true;
        unit.hidden = true;
        controls.prepend(price, unit);
        const showPrice = (variant) => {
          price.textContent = variant ? formatPrice(variant.price) : '';
          price.hidden = !variant;
          unit.textContent = unitPrice(variant);
          unit.hidden = !unit.textContent;
        };
        let chosen = variants.length === 1 ? variants[0] : null;
        showPrice(chosen);
        let select;
        if (variants.length > 1) {
          const id = `${input.id}-variant-${++cardNumber}`;
          const caption = text('label', label('variantLabel'));
          caption.htmlFor = id;
          select = document.createElement('select');
          select.id = id;
          select.append(text('option', label('variantPlaceholder')));
          select.firstElementChild.value = '';
          variants.forEach((variant) => {
            const option = text('option', variant.title + ' · ' + formatPrice(variant.price) + (unitPrice(variant) ? ' (' + unitPrice(variant) + ')' : '') + (variant.available ? '' : ' · ' + label('soldOut')));
            option.value = String(variant.id);
            option.disabled = !variant.available;
            select.append(option);
          });
          controls.prepend(caption, select);
          select.addEventListener('change', () => {
            chosen = variants.find((variant) => String(variant.id) === select.value);
            showPrice(chosen);
            button.disabled = !chosen?.available;
            message.textContent = '';
            cartLink.hidden = true;
          });
        } else if (chosen.title !== 'Default Title') {
          controls.prepend(text('p', chosen.title));
        }
        button.textContent = variants.some((variant) => variant.available) ? label('addToCart') : label('soldOut');
        button.disabled = !chosen?.available;
        let adding = false;
        button.addEventListener('click', async () => {
          if (adding || !chosen?.available) return;
          adding = true;
          button.disabled = true;
          if (select) select.disabled = true;
          button.textContent = label('addingToCart');
          message.textContent = '';
          cartLink.hidden = true;
          let confirmed = false;
          let sent = false;
          try {
            const cart = testCart ? null : document.querySelector('cart-notification, cart-drawer');
            const sections = testCart ? null : (cart?.getSectionsToRender().map((section) => section.id) || ['cart-icon-bubble']);
            const payload = { id: chosen.id, quantity: 1 };
            if (sections) {
              payload.sections = sections;
              payload.sections_url = location.pathname;
              cart?.setActiveElement(button);
            }
            sent = true;
            const added = await fetch(cartRoot + 'cart/add.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload), signal: AbortSignal.timeout(20000),
            });
            const data = await added.json();
            if (!added.ok || data.status) {
              sent = false; // A rejected request did not add an item.
              throw new Error(typeof data.description === 'string' ? data.description : label('cartError'));
            }
            if (data.id !== chosen.id || !data.quantity) throw new Error(label('cartCheck'));
            confirmed = true;
            message.textContent = testCart ? label('testCartAdded') : label('addedToCart');
            cartLink.hidden = false;
            // A rendering failure must never encourage adding the same item again.
            if (testCart) await showTestCart();
            else {
              const icon = document.getElementById('cart-icon-bubble');
              const iconMarkup = typeof data.sections?.['cart-icon-bubble'] === 'string'
                ? new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section') : null;
              if (icon && iconMarkup) icon.innerHTML = iconMarkup.innerHTML;
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nlx-advice', productVariantId: chosen.id, cartData: data })).catch(() => {});
              }
              if (cart && sections.every((id) => typeof data.sections?.[id] === 'string')) cart.renderContents(data);
            }
          } catch (error) {
            if (!confirmed) {
              message.textContent = sent ? label('cartCheck') : (error.message || label('cartError'));
              cartLink.hidden = !sent;
            }
          } finally {
            adding = false;
            button.disabled = !chosen?.available;
            if (select) select.disabled = false;
            button.textContent = label('addToCart');
          }
        });
      } catch {
        button.hidden = true;
        message.textContent = label('variantsError');
      }
    };
    let controller;
    const showFollowup = (question) => {
      const replyForm = text('form', '', 'nx-advice__reply');
      const caption = text('label', label('replyLabel'));
      const field = document.createElement('textarea');
      field.id = input.id + '-reply';
      field.name = 'reply';
      field.required = true;
      field.minLength = 3;
      field.maxLength = 700;
      field.rows = 3;
      field.placeholder = label('replyPlaceholder');
      caption.htmlFor = field.id;
      const prompt = text('p', question, 'nx-advice__followup');
      prompt.id = field.id + '-question';
      const hint = text('p', label('replyHint'), 'nx-advice__reply-hint');
      hint.id = field.id + '-hint';
      const message = text('p', '', 'nx-advice__reply-status');
      message.id = field.id + '-status';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      field.setAttribute('aria-describedby', prompt.id + ' ' + hint.id + ' ' + message.id);
      const button = text('button', label('replySubmit'));
      button.type = 'submit';
      replyForm.append(prompt, caption, field, hint, button, message);
      replyForm.addEventListener('submit', (event) => {
        event.preventDefault();
        return requestAdvice(replyForm, field, button, message);
      });
      followup = { field, button };
      result.append(replyForm);
    };
    root.querySelectorAll('[data-advice-example]').forEach((button) => button.addEventListener('click', () => {
      if (busy || preparingPhotos) return;
      input.value = button.textContent.trim();
      input.focus();
    }));
    reset.addEventListener('click', () => {
      if (busy) return;
      history.length = 0;
      followup = null;
      clearPhotos();
      result.replaceChildren();
      result.hidden = true;
      input.value = '';
      status.textContent = '';
      reset.hidden = true;
      input.focus();
    });
    const requestAdvice = async (activeForm, activeInput, activeSubmit, activeStatus = status) => {
      if (busy || preparingPhotos || !activeForm.reportValidity()) return;
      const question = activeInput.value.trim();
      if (question.length < 3 || question.length > 700) {
        activeStatus.textContent = label('questionLength');
        activeInput.focus();
        return;
      }
      const payload = { question, history: history.slice(-6), detail: detail?.value || 'detailed' };
      if (photos.length) payload.images = photos.map((photo) => photo.dataUrl);
      const body = JSON.stringify(payload);
      if (new TextEncoder().encode(body).length > maxRequestBytes) {
        activeStatus.textContent = label('requestSizeError');
        return;
      }
      busy = true;
      updatePhotoControls();
      input.disabled = true;
      const previousFollowup = followup;
      if (previousFollowup) {
        previousFollowup.field.disabled = true;
        previousFollowup.button.disabled = true;
      }
      if (detail) detail.disabled = true;
      activeSubmit.textContent = label('loading');
      root.setAttribute('aria-busy', 'true');
      status.textContent = label('loading');
      activeStatus.textContent = label('loading');
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 100000);
      try {
        const endpoint = new URL(root.dataset.endpoint, location.href);
        if (!['http:', 'https:'].includes(endpoint.protocol)) throw new Error(label('error'));
        const response = await fetch(endpoint.href, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body, signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || label('error'));
        if (payload.images?.length && data.photo_count !== payload.images.length) throw new Error(label('photosUnavailable'));
        result.replaceChildren();
        followup = null;
        result.append(text('p', question, 'nx-advice__asked'));
        result.append(text('h3', label('answerTitle')));
        data.answer.split(/\n+/).filter((paragraph) => paragraph.trim()).forEach((paragraph) => result.append(text('p', paragraph.trim())));
        if (data.question) showFollowup(data.question);
        if (data.products.length) {
          const groups = new Map();
          data.products.forEach((product) => {
            const stage = data.routine ? product.stage : '';
            if (!groups.has(stage)) {
              const group = text('div', '', 'nx-advice__group');
              if (stage) group.append(text('h4', stage, 'nx-advice__stage'));
              const grid = text('div', '', 'nx-advice__cards');
              group.append(grid);
              groups.set(stage, { group, grid });
            }
            const grid = groups.get(stage).grid;
            const card = document.createElement('article');
            card.className = 'nx-advice__card';
            if (product.kind && product.priority) card.append(text('p', product.kind + ' · ' + product.priority, 'nx-advice__badge'));
            if (product.image) {
              const imageUrl = new URL(product.image);
              if (imageUrl.protocol === 'https:' && imageUrl.hostname === 'cdn.shopify.com') {
                const image = document.createElement('img');
                image.src = imageUrl.href;
                image.alt = product.title;
                image.loading = 'lazy';
                image.width = 240;
                image.height = 240;
                card.append(image);
              }
            }
            card.append(text('h4', product.title));
            card.append(text('p', product.reason));
            addCartControls(card, product);
            const url = new URL(product.url);
            if (url.protocol === 'https:' && url.hostname === 'vfxzhj-i5.myshopify.com' && url.pathname.startsWith('/products/')) {
              const link = text('a', label('productLink') + ' ↗');
              link.href = testCart ? url.href : cartRoot + 'products/' + encodeURIComponent(product.handle);
              link.target = '_blank';
              link.rel = 'noopener';
              card.append(link);
            }
            const source = document.createElement('details');
            source.append(text('summary', label('sourceTitle')));
            source.append(text('blockquote', product.evidence));
            card.append(source);
            if (product.application || product.warnings) {
              const application = document.createElement('details');
              application.append(text('summary', label('applicationTitle')));
              if (product.application) application.append(text('p', product.application));
              if (product.warnings) application.append(text('p', product.warnings, 'nx-advice__warning'));
              card.append(application);
            }
            grid.append(card);
          });
          groups.forEach(({ group }) => result.append(group));
        }
        result.hidden = false;
        history.push({ role: 'user', content: question }, { role: 'assistant', content: [data.answer, data.question].filter(Boolean).join(' ').slice(0, 1500) });
        activeInput.value = '';
        status.textContent = data.question ? label('followupHint') : label('done');
        reset.hidden = false;
        result.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        if (followup) followup.field.focus({ preventScroll: true });
      } catch (error) {
        status.textContent = error.name === 'AbortError' ? label('timeout') : (error.message === 'Failed to fetch' || error instanceof TypeError ? label('error') : error.message);
        activeStatus.textContent = status.textContent;
      } finally {
        clearTimeout(timeout);
        busy = false;
        updatePhotoControls();
        input.disabled = false;
        if (detail) detail.disabled = false;
        if (previousFollowup) {
          previousFollowup.field.disabled = false;
          previousFollowup.button.disabled = false;
        }
        activeSubmit.textContent = activeForm === form ? submitLabel : label('replySubmit');
        root.removeAttribute('aria-busy');
      }
    };
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      return requestAdvice(form, input, submit);
    });
  };
  const start = () => document.querySelectorAll('[data-nlx-advice]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
